// ui/Table — THE table (Design Standard v1): sticky header, sortable columns, row
// hover, 40px rows (48 phone), 13px tabular-nums cells, and an empty state that says
// what to do. Two ways in:
//   <DataTable columns={[{key,label,num,sort,render}]} rows={…} rowKey="id"
//              onRowClick={…} empty={<EmptyState …/>} />
//   or the parts: <Table><THead columns/><tbody><TRow><TCell/></TRow></tbody></Table>
import React, { useMemo, useState } from "react";
import clsx from "clsx";
import { ChevronDown, ChevronUp } from "lucide-react";
import Badge from "./badge";
import EmptyState from "./EmptyState";
import "./ds.css";

export const Table = ({ children, className = "", maxHeight, ...props }) => (
  <div className="ds-table-wrap" style={maxHeight ? { maxHeight } : undefined}>
    <table className={clsx("ds-table", className)} {...props}>
      {children}
    </table>
  </div>
);

export const THead = ({ columns = [], children }) => (
  <thead>
    <tr>
      {columns.length
        ? columns.map((c, i) => (
            <th key={i} className={clsx((c.num || c.align === "right") && "is-num")}>
              {c.label ?? c}
            </th>
          ))
        : children}
    </tr>
  </thead>
);

export const TRow = ({ children, className = "", onClick, ...props }) => (
  <tr className={clsx(onClick && "is-clickable", className)} onClick={onClick} {...props}>
    {children}
  </tr>
);

export const TCell = ({ children, align, num, className = "", ...props }) => (
  <td className={clsx((num || align === "right") && "is-num", className)} {...props}>
    {children}
  </td>
);

/** Status badge slot — converged into ui/Badge. status ∈ success | warning | danger | neutral */
export const TStatus = ({ status = "neutral", children }) => <Badge tone={status}>{children}</Badge>;

export const TEmpty = ({ colSpan, children = "Nothing here yet." }) => (
  <tr className="ds-table__empty">
    <td colSpan={colSpan}>
      {typeof children === "string" ? <EmptyState body={children} /> : children}
    </td>
  </tr>
);

const cmp = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return 1; // unknowns sort last, never as 0
  if (b == null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
};

export function DataTable({ columns, rows = [], rowKey = "id", onRowClick, empty, initialSort, maxHeight, className = "" }) {
  const [sort, setSort] = useState(initialSort || null); // { key, dir: 'asc'|'desc' }
  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    const get = col?.sortValue || ((r) => r[sort.key]);
    const out = [...rows].sort((a, b) => cmp(get(a), get(b)));
    return sort.dir === "desc" ? out.reverse() : out;
  }, [rows, sort, columns]);
  const toggle = (key) =>
    setSort((s) => (!s || s.key !== key ? { key, dir: "asc" } : s.dir === "asc" ? { key, dir: "desc" } : null));
  return (
    <Table maxHeight={maxHeight} className={className}>
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c.key} className={clsx(c.num && "is-num")}
              aria-sort={sort?.key === c.key ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}>
              {c.sort === false ? c.label : (
                <button type="button" className="ds-table__sort" onClick={() => toggle(c.key)}>
                  {c.label}
                  {sort?.key === c.key ? (sort.dir === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />) : null}
                </button>
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sorted.length === 0 ? (
          <TEmpty colSpan={columns.length}>{empty || "Nothing here yet."}</TEmpty>
        ) : (
          sorted.map((r, i) => (
            <TRow key={typeof rowKey === "function" ? rowKey(r) : r[rowKey] ?? i} onClick={onRowClick ? () => onRowClick(r) : undefined}>
              {columns.map((c) => (
                <TCell key={c.key} num={c.num}>{c.render ? c.render(r) : r[c.key] ?? "—"}</TCell>
              ))}
            </TRow>
          ))
        )}
      </tbody>
    </Table>
  );
}
